import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import RHB_LOGO from '../../../../assets/logos/rhb.webp';
import UOB_LOGO from '../../../../assets/logos/uob.png';
import { PAYMENT_METHOD_CONFIG } from '../constants/payment-method.const';

const PUBLIC = process.env.PUBLIC_URL || '';

const CARD_LOGOS = [
  { src: PAYMENT_METHOD_CONFIG.mastercard.logo, alt: 'Mastercard' },
  { src: PAYMENT_METHOD_CONFIG.visa.logo, alt: 'Visa' },
  { src: PAYMENT_METHOD_CONFIG.unionpay.logo, alt: 'UnionPay' },
  { src: PAYMENT_METHOD_CONFIG.amex.logo, alt: 'AMEX' }
];

const EWALLET_LOGOS = [
  { src: PAYMENT_METHOD_CONFIG.grabpay.logo, alt: 'GrabPay' },
  { src: PAYMENT_METHOD_CONFIG.tng.logo, alt: 'TNG' },
  { src: PAYMENT_METHOD_CONFIG.shopeepay.logo, alt: 'ShopeePay' }
];

const DUITNOW_LOGO = `${PUBLIC}/assets/logos/duitnow-logo.png`;

const ChevronRight = () => (
  <svg
    className="h-6 w-6 text-gray-500"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="m10 16 4-4-4-4"
    />
  </svg>
);

interface SelectPaymentTypeStepProps {
  onSelectCards: () => void;
  onSelectEWallets: () => void;
  onSkipPayment: () => void;
  onBack: () => void;
  isEWalletEnabled: boolean;
  /** Label for the back button. Pass null to hide it entirely. Defaults to "Go back to enter Account Details" */
  backLabel?: string | null;
  /** Remove top margin from the header section (e.g. when rendered inside a dialog). Defaults to false. */
  noTopMargin?: boolean;
}

export function SelectPaymentTypeStep({
  onSelectCards,
  onSelectEWallets,
  onBack,
  isEWalletEnabled,
  backLabel = 'Go back to enter Account Details',
  noTopMargin = false
}: Readonly<SelectPaymentTypeStepProps>) {
  return (
    <div className="w-full max-w-md px-6 sm:px-0">
      <div className={noTopMargin ? 'mt-0' : 'mt-8'}>
        {backLabel !== null && (
          <button
            type="button"
            onClick={onBack}
            className="-ml-1 mb-3 flex items-center gap-1 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Account Details</span>
          </button>
        )}
        <h1 className="text-2xl font-bold text-gray-900">
          Select a Payment Method
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          Choose which payment method you'd like to link with your face.
        </p>
      </div>

      <div className="mt-8 flex flex-col gap-3">
        {/* Cards */}
        <button
          type="button"
          onClick={onSelectCards}
          className="flex w-full items-center justify-between rounded-2xl border-2 border-gray-200 p-4 text-left shadow-sm transition-all hover:border-[#5CB891] hover:shadow-md"
        >
          <div className="flex flex-col gap-3">
            <span className="text-base font-semibold text-gray-900">Cards</span>
            <div className="flex flex-row items-start gap-2">
              {CARD_LOGOS.map((logo) => (
                <div key={logo.alt} className="my-1">
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className="h-6 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
          <ChevronRight />
        </button>

        {/* eWallets */}
        <button
          type="button"
          onClick={isEWalletEnabled ? onSelectEWallets : undefined}
          disabled={!isEWalletEnabled}
          className={`flex w-full items-center justify-between rounded-2xl border-2 p-4 text-left shadow-sm transition-all ${
            isEWalletEnabled
              ? 'border-gray-200 hover:border-[#5CB891] hover:shadow-md'
              : 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
          }`}
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold text-gray-900">
                eWallets
              </span>
              {!isEWalletEnabled && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-400/20">
                  Coming Soon
                </span>
              )}
            </div>
            <div className="flex flex-row items-start gap-2">
              {EWALLET_LOGOS.map((logo) => (
                <div key={logo.alt} className="my-1">
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className="h-6 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
          <ChevronRight />
        </button>

        {/* AutoDebit — always coming soon */}
        <div className="flex w-full cursor-not-allowed items-center justify-between rounded-2xl border-2 border-gray-200 bg-gray-50 p-4 opacity-70">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <img
                src={DUITNOW_LOGO}
                alt="DuitNow"
                className="h-8 object-contain"
              />
              <span className="text-base font-bold text-gray-700">
                AutoDebit
              </span>
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-400/20">
                Coming Soon
              </span>
            </div>
            <div className="flex flex-row items-center gap-2 pl-1">
              <span className="text-xs text-gray-500">supported banks:</span>
              <img src={UOB_LOGO} alt="UOB" className="h-4 object-contain" />
              <img src={RHB_LOGO} alt="RHB" className="h-4 object-contain" />
            </div>
          </div>
          <ChevronRight />
        </div>
      </div>

      {/* Footer actions */}
      {backLabel !== null && (
        <div className="mt-12 flex justify-center">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-auto w-4" />
            <span>{backLabel}</span>
          </button>
        </div>
      )}
    </div>
  );
}
